<script setup lang="ts">
// @ts-nocheck
import BaykusAvatar from '@/components/BaykusAvatar/BaykusAvatar.vue'
import VueEasyLightbox from 'vue-easy-lightbox'

import { ref, onMounted } from 'vue'
import apiClient from '@/services/axios'
import { useUserStore } from '@/stores/user'
import { DateTime } from 'luxon'
const { getUserData, getTokens } = useUserStore()

export interface Id {
  timestamp: number
  machine: number
  pid: number
  increment: number
  creationTime: string
}

export interface ConversationId {
  timestamp: number
  machine: number
  pid: number
  increment: number
  creationTime: string
}

export interface SystemMessage {
  systemMessageType: string
  textForSender: string
  textForReceiver: string
  invitationId: string
  date: string
  time: string
  invitationType: string
  excuse: string
}
export interface Message {
  id: Id
  senderId: string
  conversationId: ConversationId
  receiverId: string
  content: string
  timestamp: string
  systemMessage: SystemMessage | null
  message?: string | null
  excuse?: string
  mediaAttachment?: {
    contentType: string
    fileUrl: string
    previewDimensions: string
    previewUrl: string
  }
}
type Chat = Message & {
  profilePhoto: string
  studentFullName: string
  lastMessage: string
  lastMessageOwner: string
  lastMessageDate: string
  position: string
}
type Props = {
  message: Message
  chat: Chat
}
const props = defineProps<Props>()
const audioSrc = ref('')
const imageSrc = ref('')
const loadingImage = ref(false)
const fullImageSrc = ref('')
const getProfilePhoto = (itemSenderId: string) => {
  if (itemSenderId === getUserData.value.id) {
    return getUserData.value.profilePhotoUrl
  }
  return props.chat.profilePhoto
}
const fetchAudioFile = async () => {
  try {
    const response = await apiClient.get(props.message.mediaAttachment.fileUrl, {
      responseType: 'blob'
    })
    audioSrc.value = URL.createObjectURL(response.data)
  } catch (error) {
    console.error('Audio file could not be loaded:', error)
  }
}
const parseSystemMessage = (par: any) => {
  let data: Message = {} as Message

  if (par.systemMessage.systemMessageType) {
    data = {
      ...data,
      ...par.systemMessage,
      message: par.systemMessage
    }

    if (props.message.senderId === getUserData.value.id) {
      data = {
        ...data,
        message: par.systemMessage.textForSender
      }
    }
    if (props.message.senderId !== getUserData.value.id) {
      data = {
        ...data,
        message: par.systemMessage.textForReceiver
      }
    }
  }
  return data
}
const fetchImageFile = async () => {
  try {
    const response = await apiClient.get(props.message.mediaAttachment.previewUrl, {
      responseType: 'blob'
    })
    imageSrc.value = URL.createObjectURL(response.data)
  } catch (error) {
    console.error('Image file could not be loaded:', error)
  }
}
const onOpenImage = async () => {
  try {
    loadingImage.value = true
    const response = await apiClient.get(props.message.mediaAttachment?.fileUrl, {
      responseType: 'blob'
    })
    fullImageSrc.value = URL.createObjectURL(response.data)
    visibleRef.value = true
  } catch (error) {
    console.error('Image file could not be loaded:', error)
  } finally {
    loadingImage.value = false
  }
}
onMounted(() => {
  if (props.message.mediaAttachment?.contentType.includes('audio')) {
    fetchAudioFile()
  }
  if (props.message.mediaAttachment?.contentType.includes('image')) {
    fetchImageFile()
  }
})

const visibleRef = ref(false)
</script>
<template>
  <div v-if="props.message">
    <Teleport to="body">
      <div>
        <div
          v-if="loadingImage"
          class="w-full fixed z-50 top-0 left-0 h-full bg-black/50 flex items-center justify-center"
        >
          <div class="w-[24px] h-[24px] border-2 border-purple-500 rounded-full animate-spin"></div>
        </div>
      </div>
      <vue-easy-lightbox
        @hide="visibleRef = false"
        :visible="visibleRef"
        :imgs="fullImageSrc"
      ></vue-easy-lightbox>
    </Teleport>
    <div class="flex items-start gap-3 mb-[16px]">
      <div>
        <BaykusAvatar
          :src="getProfilePhoto(props.message.senderId) as string"
          width="32"
          height="32"
        />
      </div>

      <div class="flex flex-col items-start justify-start">
        <div>
          <span class="text-sm text-gray-700 font-semibold">{{
            message.senderId === getUserData.id
              ? `${getUserData.name} ${getUserData.surname}`
              : chat.studentFullName
          }}</span>
          <span class="text-sm text-gray-500 ml-1">{{
            DateTime.fromISO(props.message.timestamp).toFormat('HH:mm')
          }}</span>
        </div>
        <span
          v-linkify="message.content"
          v-if="
            !props.message.systemMessage?.systemMessageType &&
            !props.message.mediaAttachment?.contentType
          "
          class="text-sm text-gray-700 break-all"
        >
          {{ props.message.content }}
        </span>
        <div
          class="-ml-[40px] mt-[15px]"
          v-if="
            props.message.mediaAttachment?.fileUrl &&
            props.message.mediaAttachment?.contentType.includes('audio')
          "
        >
          <audio v-if="audioSrc" class="max-w-[315px]" controls>
            <source :src="audioSrc" type="audio/mp3" />
          </audio>
        </div>
        <div
          class="mt-[10px]"
          v-if="
            props.message.mediaAttachment?.fileUrl &&
            props.message.mediaAttachment?.contentType.includes('image')
          "
        >
          <div
            @click="onOpenImage"
            class="w-[248px] h-[156px] overflow-hidden flex items-center justify-center cursor-pointer rounded-md"
          >
            <img v-if="imageSrc" class="h-auto w-full" :src="imageSrc" />
          </div>
        </div>
        <div
          class="bg-purple-100 mt-2 flex flex-col justify-center items-center p-5 rounded-md"
          v-if="props.message.systemMessage?.systemMessageType"
        >
          <div class="icon mb-2">
            <svg
              v-if="props.message.systemMessage.invitationType === 'VoiceCall'"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 14.5125L16.5853 12.5334L16.5684 12.5259C16.2815 12.4022 15.9681 12.3524 15.6569 12.3811C15.3458 12.4097 15.0467 12.5159 14.7872 12.69C14.7508 12.7143 14.7158 12.7406 14.6822 12.7687L12.5906 14.55C11.3719 13.889 10.1128 12.6403 9.45094 11.4365L11.2378 9.31217C11.2665 9.27789 11.2932 9.24188 11.3175 9.20436C11.487 8.94576 11.5899 8.64925 11.617 8.34125C11.6442 8.03325 11.5948 7.72332 11.4731 7.43905C11.4702 7.4336 11.4677 7.42796 11.4656 7.42217L9.48751 2.99999C9.32513 2.63016 9.04801 2.32244 8.69716 2.12236C8.3463 1.92228 7.94035 1.84048 7.53938 1.88905C6.17848 2.06782 4.92918 2.73587 4.02482 3.76841C3.12046 4.80096 2.62288 6.12739 2.62501 7.49999C2.62501 15.1509 8.84907 21.375 16.5 21.375C17.8726 21.3771 19.199 20.8795 20.2316 19.9752C21.2641 19.0708 21.9322 17.8215 22.1109 16.4606C22.1595 16.0596 22.0777 15.6537 21.8776 15.3028C21.6775 14.952 21.3698 14.6749 21 14.5125ZM16.5 19.125C13.418 19.1213 10.4633 17.8953 8.284 15.716C6.1047 13.5367 4.87873 10.582 4.87501 7.49999C4.87286 6.72326 5.13655 5.96919 5.62227 5.36306C6.10799 4.75694 6.78648 4.33527 7.54501 4.16811L9.30938 8.10561L7.51313 10.245C7.4841 10.2796 7.45717 10.3159 7.43251 10.3537C7.25546 10.6243 7.15137 10.936 7.13034 11.2586C7.10932 11.5812 7.17207 11.9038 7.31251 12.195C8.19563 14.0025 10.0153 15.81 11.8416 16.695C12.1347 16.834 12.4589 16.8945 12.7825 16.8707C13.106 16.8468 13.4179 16.7395 13.6875 16.559C13.7238 16.5346 13.7585 16.508 13.7916 16.4794L15.8944 14.6915L19.8319 16.455C19.6647 17.2135 19.243 17.892 18.6369 18.3777C18.0308 18.8634 17.2767 19.1271 16.5 19.125Z"
                fill="#5E36FE"
              />
            </svg>
            <svg
              v-else
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_24_1679)">
                <path d="M0 0H24V24H0V0Z" fill="#E7E0FF" />
                <path
                  d="M16 4C16.552 4 17 4.448 17 5V9.2L22.213 5.55C22.439 5.392 22.751 5.447 22.91 5.674C22.968 5.758 23 5.858 23 5.96V18.04C23 18.316 22.776 18.54 22.5 18.54C22.397 18.54 22.297 18.508 22.213 18.45L17 14.8V19C17 19.552 16.552 20 16 20H2C1.448 20 1 19.552 1 19V5C1 4.448 1.448 4 2 4H16ZM15 6H3V18H15V6ZM8 8H10V11H13V13H9.999L10 16H8L7.999 13H5V11H8V8ZM21 8.841L17 11.641V12.359L21 15.159V8.841Z"
                  fill="#5E36FE"
                />
              </g>
              <defs>
                <clipPath id="clip0_24_1679">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>
          <span class="text-sm text-center text-gray-700">
            {{ parseSystemMessage(props.message).message }}
          </span>
          <span
            v-if="parseSystemMessage(props.message).excuse"
            class="text-sm text-center text-gray-700 mt-2"
          >
            {{ parseSystemMessage(props.message).excuse }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
